import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../hooks/hooks";
import { RootState } from "../redux/store";
import { buildLoginPath } from "../utils/returnPath";

interface Props {
    children: React.ReactNode;
}

// localStorage access can throw (Safari private browsing, storage disabled by
// policy); treat that as "no token", the same way ProtectedRoute does.
const readStoredToken = (): string | null => {
    try {
        return localStorage.getItem("access_token");
    } catch {
        return null;
    }
};

const AdminRoute: React.FC<Props> = ({ children }) => {
    const user = useAppSelector((state: RootState) => state.auth.user);
    const isAdmin = useAppSelector((state: RootState) => state.auth.adminMode);
    const isSuperAdmin = useAppSelector(
        (state: RootState) => state.auth.superAdminMode
    );
    const isp3mSuperAdmin = useAppSelector(
        (state: RootState) => state.auth.p3mSuperAdminMode
    );
    const isOwner = useAppSelector((state: RootState) => state.auth.ownerMode);
    const isEvaluator = useAppSelector((state: RootState) =>
        state?.auth?.user?.role?.includes("Evaluator")
    );
    const isAuditor = useAppSelector((state: RootState) =>
        state?.auth?.user?.role?.includes("Auditor")
    );
    const adminFeatures = useAppSelector(
        (state: RootState) => state?.auth?.user?.adminFeaturesAllowed
    );
    const features = [
        "TaskAndGoals",
        "ManageAuditee",
        "ResourceHub",
        "NoticeBoard",
        "DepartmentAndUnit",
    ];
    const evaluatorHasPermittedFeature = features.some((feature) =>
        adminFeatures?.includes(feature)
    );
    const evaluator = isEvaluator && evaluatorHasPermittedFeature;
    const hasAnecdotalRecordsAccess = adminFeatures?.includes("AnecdotalRecords");

    const location = useLocation();

    const hasAccess =
        isSuperAdmin ||
        isAdmin ||
        isOwner ||
        evaluator ||
        isp3mSuperAdmin ||
        isAuditor ||
        hasAnecdotalRecordsAccess;

    // On a direct navigation / hard refresh the Redux auth state starts empty
    // and the profile (which sets adminMode/ownerMode/etc.) is hydrated
    // asynchronously by `loaduser`. If a token exists in storage but the user
    // hasn't loaded yet, wait for that bootstrap to finish instead of bouncing
    // away. A 401 during loaduser clears the token (via the global
    // interceptor), which then flips this to the redirect branches below.
    const token = readStoredToken();

    if (token && !user) {
        return <div>Loading...</div>;
    }

    if (hasAccess) {
        return <>{children}</>;
    }

    // Signed in, but this role may not open the page. Send them Home rather
    // than to login: the login route would immediately forward an
    // authenticated user back to `?redirect=`, which would loop through here
    // forever.
    if (user) {
        return <Navigate to="/" replace={true} />;
    }

    // No session at all: go to login and carry the page along so a successful
    // sign-in returns here.
    return <Navigate to={buildLoginPath(location)} replace={true} />;
};

export default AdminRoute;
